/**
 * Centralized API service layer.
 * All server calls should go through this module so error handling,
 * base URLs, and auth headers are managed in one place.
 *
 * The `api` axios instance is defined in AuthContext and re-exported here
 * so that all callers share the same interceptors (auth header, 401 logout).
 */
import axios from 'axios';
import { User, EmailTemplate, EmailSendRow, EmailQueueRow, DomainInvestigationResult, SegmentDomainResult, UnifiedDomainResult, SendStats, SendRecipientRow, DashboardSummary, StaggerResult, HostlinksEvent, EventCampaignLink, EventBuildResult, ZoomPreview, ZoomQueueSendResult } from '../types';
// Use the global axios instance — interceptors (auth header, 401 logout) are
// registered at module level in AuthContext.tsx and apply to all axios calls.
const api = axios;

// ---------- Auth ----------

export const authApi = {
  login: (emailOrUsername: string, password: string, deviceToken?: string) =>
    api.post<{
      user?: User;
      token?: string;
      mfaRequired?: boolean;
      mfaToken?: string;
      emailHint?: string;
    }>('/api/auth/login', { emailOrUsername, password, deviceToken }),

  verifyMfa: (mfaToken: string, code: string, rememberDevice: boolean) =>
    api.post<{ user: User; token: string; deviceToken?: string }>('/api/auth/login/verify', {
      mfaToken,
      code,
      rememberDevice,
    }),

  resendMfa: (mfaToken: string) =>
    api.post<{ emailHint?: string }>('/api/auth/login/resend', { mfaToken }),

  me: () => api.get<User>('/api/auth/me'),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/api/auth/change-password', { currentPassword, newPassword }),

  exchangeOAuthCode: (code: string) =>
    api.post<{ token: string }>('/api/auth/exchange', { code }),
};

// ---------- Users ----------

export const usersApi = {
  list: () => api.get<User[]>('/api/users'),

  create: (data: { username: string; email: string; password: string; role?: string }) =>
    api.post<User>('/api/users', data),

  resetPassword: (id: string, password: string) =>
    api.post(`/api/users/${id}/reset-password`, { password }),

  updateRole: (id: string, role: 'admin' | 'user') =>
    api.patch(`/api/users/${id}/role`, { role }),

  delete: (id: string) => api.delete(`/api/users/${id}`),
};

// ---------- Templates ----------

export const templatesApi = {
  list: () => api.get<EmailTemplate[]>('/api/templates'),

  get: (id: string) => api.get<EmailTemplate>(`/api/templates/${id}`),

  create: (data: Partial<EmailTemplate>) => api.post<EmailTemplate>('/api/templates', data),

  update: (id: string, data: Partial<EmailTemplate>) =>
    api.put<EmailTemplate>(`/api/templates/${id}`, data),

  delete: (id: string) => api.delete(`/api/templates/${id}`),
};

// ---------- Interspire ----------

export const interspireApi = {
  getLists: () => api.get('/api/interspire/lists'),

  getSegments: () => api.get('/api/interspire/segments'),

  getCustomFields: () => api.get('/api/interspire/customfields'),

  calculateSegment: (segmentId: number) =>
    api.post(`/api/interspire/calculate/${segmentId}`),

  getListContacts: (
    listId: number,
    params: { page?: number; limit?: number; q?: string; fieldId?: number; fieldValue?: string; status?: string }
  ) => api.get(`/api/interspire/lists/${listId}/contacts`, { params }),

  addContact: (listId: number, data: { emailaddress: string; fields?: Record<string, string> }) =>
    api.post(`/api/interspire/lists/${listId}/contacts`, data),

  updateContact: (
    listId: number,
    subscriberId: number,
    data: { emailaddress?: string; confirmed?: boolean; unsubscribed?: boolean; fields?: Record<string, string> }
  ) => api.patch(`/api/interspire/lists/${listId}/contacts/${subscriberId}`, data),

  deleteContact: (listId: number, subscriberId: number) =>
    api.delete(`/api/interspire/lists/${listId}/contacts/${subscriberId}`),

  queueCampaigns: (data: unknown) => api.post('/api/interspire/queue', data),

  getSendHistory: (forceRefresh = false) =>
    api.get<{ rows: EmailSendRow[]; cacheTtlMins: number }>('/api/interspire/sends', {
      params: forceRefresh ? { refresh: 'true' } : undefined,
    }),

  getSegmentSize: (segmentId: number) =>
    api.get<{ count: number; listId: number }>(`/api/interspire/segments/${segmentId}/size`),

  investigateDomain: (statid: number, domain: string, samples?: boolean, segmentId?: number | null) =>
    api.post<UnifiedDomainResult>(`/api/interspire/sends/${statid}/domain`, {
      domain,
      ...(samples ? { samples } : {}),
      ...(segmentId != null ? { segmentId } : {}),
    }),

  investigateDomainForSegment: (segmentId: number, domain: string, samples?: boolean) =>
    api.post<UnifiedDomainResult>(`/api/interspire/segments/${segmentId}/domain`, { domain, samples }),

  getSendStats: (statid: number) =>
    api.get<SendStats>(`/api/interspire/sends/${statid}/stats`),

  getSendRecipients: (statid: number, type: 'all' | 'bounced' | 'unsubscribed', page: number, limit = 50) =>
    api.get<{ rows: SendRecipientRow[]; total: number }>(
      `/api/interspire/sends/${statid}/recipients`,
      { params: { type, page, limit } }
    ),

  getSendRecipientsExport: (statid: number, type: 'all' | 'bounced' | 'unsubscribed') =>
    api.get<{ rows: SendRecipientRow[]; total: number }>(
      `/api/interspire/sends/${statid}/recipients`,
      { params: { type, page: 1, limit: 10000, export: 'true' } }
    ),

  getSendPreview: (statid: number) =>
    api.get<{ campaignName: string; subject: string | null; htmlBody: string | null; textBody: string | null; newsletterid: number }>(
      `/api/interspire/sends/${statid}/preview`
    ),

  getEmailQueue: (forceRefresh = false) =>
    api.get<{ rows: EmailQueueRow[]; cacheTtlMins: number }>('/api/interspire/queue', {
      params: forceRefresh ? { refresh: 'true' } : undefined,
    }),

  previewScheduleStagger: () =>
    api.post<StaggerResult>('/api/interspire/queue/stagger/preview'),

  applyScheduleStagger: (jobids: number[]) =>
    api.post<StaggerResult>('/api/interspire/queue/stagger/apply', { jobids }),

  rescheduleQueueJob: (jobid: number, scheduledUnix: number) =>
    api.patch<{ jobid: number; scheduledDate: number }>(
      `/api/interspire/queue/${jobid}/schedule`,
      { scheduledUnix }
    ),

  cloneQueueJob: (jobid: number, data: { campaignName: string; scheduledUnix: number }) =>
    api.post<{
      jobid: number;
      newsletterid: number;
      campaignName: string;
      scheduledDate: number;
    }>(`/api/interspire/queue/${jobid}/clone`, data),

  getQueuePreview: (jobid: number) =>
    api.get<{ campaignName: string; subject: string | null; htmlBody: string | null; textBody: string | null; newsletterid: number }>(
      `/api/interspire/queue/${jobid}/preview`
    ),

  saveNewsletterContent: (newsletterid: number, fields: { subject?: string; htmlBody?: string; textBody?: string }) =>
    api.put<{ ok: boolean }>(`/api/interspire/newsletter/${newsletterid}`, fields),

  getDashboard: (days: number = 30) =>
    api.get<DashboardSummary>(`/api/interspire/dashboard`, { params: { days } }),
};

// ---------- Settings ----------

export const settingsApi = {
  getSmtp: () => api.get('/api/settings/smtp'),
  updateSmtp: (data: unknown) => api.put('/api/settings/smtp', data),

  getEmailConfig: () => api.get('/api/settings/email-config'),
  updateEmailConfig: (data: unknown) => api.put('/api/settings/email-config', data),

  getZeroBounce: () => api.get('/api/settings/zerobounce'),
  updateZeroBounce: (data: unknown) => api.put('/api/settings/zerobounce', data),

  getSmtpCom: () => api.get('/api/settings/smtpcom'),
  updateSmtpCom: (data: unknown) => api.post('/api/settings/smtpcom', data),

  getHostlinks: () => api.get('/api/settings/hostlinks'),
  updateHostlinks: (data: unknown) => api.post('/api/settings/hostlinks', data),
  regenerateHostlinksIngestKey: () => api.post('/api/settings/hostlinks/regenerate-ingest-key'),
};

// ---------- Events (Hostlinks) ----------

export const HOSTLINKS_UPCOMING_DAYS = 360;

export const eventsApi = {
  getUpcoming: () =>
    api.get<{
      events: HostlinksEvent[];
      source?: 'browser-sync' | 'plugin-push' | null;
      updatedAt?: string | null;
      warning?: string;
    }>('/api/events/upcoming', {
      timeout: 15_000,
    }),

  getBrowserSyncCredentials: () =>
    api.get<{ baseUrl: string; apiKey: string; days: number; eventsPath: string }>(
      '/api/events/browser-sync/credentials'
    ),

  postBrowserSync: (data: { days?: number; events: unknown[] }) =>
    api.post<{ ok: boolean; count: number; updatedAt: string; cachedAt: string; source: string }>(
      '/api/events/browser-sync',
      data
    ),

  getCampaigns: (hostlinksEventId: string) =>
    api.get<EventCampaignLink[]>(`/api/events/${encodeURIComponent(hostlinksEventId)}/campaigns`),

  buildCampaigns: (hostlinksEventId: string, data: unknown) =>
    api.post<{ results: EventBuildResult[] }>(
      `/api/events/${encodeURIComponent(hostlinksEventId)}/build-campaigns`,
      data
    ),

  getZoomPreview: () =>
    api.get<ZoomPreview>('/api/events/zoom-preview', { timeout: 20_000 }),

  queueZoomCampaigns: (data: {
    subject: string;
    html: string;
    sends: Array<{ sendIndex: 1 | 2 | 3; date: string; time: string; segmentId: number }>;
  }) =>
    api.post<{
      ok: boolean;
      partial?: boolean;
      results: ZoomQueueSendResult[];
      warning?: string;
    }>('/api/events/zoom-queue', data, { timeout: 120_000 }),
};

// ---------- Health ----------

export const healthApi = {
  check: () => api.get('/api/health'),
};

export default api;
